/**
 * ISO Data Core — CDN-backed country/region data (pure functions, no React).
 * Call ensureLoaded() before using synchronous getters.
 */

import {
    getCountries,
    getStatesForCountry,
    ensureGeoDataLoaded,
    type SupportedLanguage,
    importLanguage,
    isLanguageRegistered,
    getRegisteredLanguages
} from '../../data/iso3166';

// Types for ISO data (matching the original)
export interface ISOCountry {
    iso: string;
    iso3: string;
    numeric: number;
    name: string;
}

export interface ISORegion {
    iso: string;
    name: string;
}

export class ISODataCore {
    /**
     * Get countries data for a specific language
     */
    static getCountriesData(language: SupportedLanguage = 'en'): Record<string, ISOCountry> {
        try {
            // Get countries from pre-built data with language support
            const countriesArray = getCountries(language);

            // Transform to our expected format (Record<string, ISOCountry>)
            const countries: Record<string, ISOCountry> = {};

            countriesArray.forEach((country) => {
                countries[country.code] = {
                    iso: country.code,
                    iso3: country.iso3 || '',
                    numeric: country.numeric || 0,
                    name: country.name,
                };
            });

            return countries;
        } catch (error) {
            console.error(`Failed to load ISO data for language: ${language}`, error);
            return {};
        }
    }

    /**
     * Get regions for a specific country
     */
    static getRegions(countryCode: string, language: SupportedLanguage = 'en'): ISORegion[] {
        try {
            const states = getStatesForCountry(countryCode, language);
            return states.map((state) => ({
                iso: state.code,
                name: state.name,
            }));
        } catch {
            return []; // Return empty array if no regions
        }
    }

    /**
     * Find a specific region by ISO code
     */
    static findRegion(countryCode: string, regionCode: string, language: SupportedLanguage = 'en'): ISORegion | null {
        const regions = this.getRegions(countryCode, language);
        return regions.find((region) => region.iso === regionCode) ?? null;
    }

    /**
     * Map Google Places state to ISO region (proven 100% success rate)
     */
    static mapGoogleToISO(
        googleState: string,
        googleStateLong: string,
        countryCode: string,
        language: SupportedLanguage = 'en'
    ): ISORegion | null {
        const regions = this.getRegions(countryCode, language);

        if (regions.length === 0) return null;

        // Strategy 1: Exact ISO code match (86% success rate)
        let match = regions.find((r) => r.iso === googleState);
        if (match) return match;

        // Strategy 2: Name matching (14% success rate)
        match = regions.find((r) => r.name.toLowerCase() === googleState.toLowerCase());
        if (match) return match;

        match = regions.find((r) => r.name.toLowerCase() === googleStateLong.toLowerCase());
        if (match) return match;

        // Strategy 3: Partial name matching (fallback)
        match = regions.find(
            (r) =>
                r.name.toLowerCase().includes(googleStateLong.toLowerCase()) ||
                googleStateLong.toLowerCase().includes(r.name.toLowerCase()),
        );

        return match ?? null;
    }

    /**
     * Check if language is registered
     */
    static isLanguageRegistered(language: SupportedLanguage): boolean {
        return isLanguageRegistered(language);
    }

    /**
     * Get registered languages
     */
    static getRegisteredLanguages(): SupportedLanguage[] {
        return getRegisteredLanguages();
    }

    /**
     * Import language data
     */
    static async importLanguage(language: SupportedLanguage): Promise<void> {
        return importLanguage(language);
    }

    /**
     * Fetch countries + regions from CDN for a language (call before sync getters)
     */
    static async ensureLoaded(language: SupportedLanguage = 'en'): Promise<void> {
        return ensureGeoDataLoaded(language);
    }

    static getAvailableLanguages(): SupportedLanguage[] {
        return ['en', 'ru', 'de', 'fr', 'es', 'zh', 'hi', 'pt', 'ja', 'ar', 'it', 'he'];
    }
}
